import {Component, Inject} from '@angular/core';
import {MAT_DIALOG_DATA, MatDialogRef} from '@angular/material/dialog';

import * as moment from 'moment';

import {AccountOnboardingInProgressInterface} from '@spa/api';

import {Helper} from 'appShared/classes';
import {DialogActionsEnum, DialogButtonTypesEnum, DialogComponent, DialogConfigInterface} from 'appShared/components';
import {environment} from 'appEnv/environment';

export interface AccountOnboardingInProgressDialogData {
    onboardings: AccountOnboardingInProgressInterface[];
}

/** `{view}` opens that onboarding's progress, `{startNew}` starts another one, null cancels. */
export type AccountOnboardingInProgressDialogResult = {view: string} | {startNew: true} | null;

export interface AccountOnboardingInProgressRow {
    onboardingId: string;
    description: string;
}

/** "Started 14:02 — 3 trading accounts, new cash account"; the date is shown too when it was not today. */
export function accountOnboardingInProgressDescription(onboarding: AccountOnboardingInProgressInterface): string {
    const startedToday = moment(onboarding.created_at).isSame(moment(), 'day');
    const started = startedToday
        ? Helper.formatDate(onboarding.created_at, 'HH:mm')
        : Helper.formatDate(onboarding.created_at, `${environment.dateFormat} HH:mm`);
    const count = onboarding.trading_account_count || 0;
    const parts = [`${count} trading account${count === 1 ? '' : 's'}`];

    if (onboarding.has_new_cash_account) {
        parts.push('new cash account');
    }

    return `Started ${started} — ${parts.join(', ')}`;
}

@Component({
    selector: 'app-account-onboarding-in-progress',
    templateUrl: './account-onboarding-in-progress.component.html',
    styleUrls: ['./account-onboarding-in-progress.component.scss']
})
export class AccountOnboardingInProgressComponent extends DialogComponent<AccountOnboardingInProgressComponent> {
    public readonly rows: AccountOnboardingInProgressRow[];

    constructor(
        @Inject(MAT_DIALOG_DATA) public data: AccountOnboardingInProgressDialogData,
        public dialogRef: MatDialogRef<AccountOnboardingInProgressComponent, AccountOnboardingInProgressDialogResult>
    ) {
        super(dialogRef);

        this.config = this.buildConfig();
        this.rows = (data?.onboardings || []).map((onboarding: AccountOnboardingInProgressInterface) => ({
            onboardingId: onboarding.onboarding_id,
            description: accountOnboardingInProgressDescription(onboarding)
        }));
    }

    onActionButtonClicked(action: number): void {
        if (action === DialogActionsEnum.Confirm) {
            this.startNew();

            return;
        }

        this.dialogRef.close(null);
    }

    view(onboardingId: string): void {
        this.dialogRef.close({view: onboardingId});
    }

    startNew(): void {
        this.dialogRef.close({startNew: true});
    }

    private buildConfig(): DialogConfigInterface {
        return {
            process: {loading: false, finished: false, failed: false, succeedMessage: '', failedMessage: ''},
            buttons: [
                {type: DialogButtonTypesEnum.Cancel, cssClass: 'btn-outline-secondary'},
                {type: DialogButtonTypesEnum.Confirm, customText: 'Start a new onboarding'}
            ],
            buttonsDisabled: false,
            buttonsHidden: false
        };
    }
}
